import { Controller } from "@hotwired/stimulus"
import {
  validateImageFile,
  uploadImage,
  fetchLibrary,
  openLibraryPicker,
  ACCEPTED_IMAGE_TYPES,
  MAX_IMAGE_SIZE
} from "../modules/panel/image_upload"

const DRAG_ACTIVE_CLASSES = ["border-indigo-500", "bg-indigo-50"]

// Image field with drag-drop / file-picker upload, plus a picker that reuses
// images already uploaded to the site. Writes the chosen image's URL into the
// URL input and dispatches input/change so any form-level listeners (e.g. block
// live preview) pick up the new value.
export default class extends Controller {
  static targets = ["input", "fileInput", "dropZone", "preview", "previewImage", "error", "status"]
  static values = {
    url: { type: String, default: "/panel/image_uploads" },
    libraryUrl: { type: String, default: "/panel/site_assets" },
    context: { type: Object, default: {} },
    previewBase: { type: String, default: "" },
    maxSize: { type: Number, default: MAX_IMAGE_SIZE },
    acceptedTypes: { type: Array, default: ACCEPTED_IMAGE_TYPES }
  }

  connect() {
    this.beforeCache = () => {
      this.setBusy(false)
      this.closeLibrary?.()
    }
    document.addEventListener("turbo:before-cache", this.beforeCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    this.closeLibrary?.()
    this.revokeObjectUrl()
  }

  openPicker() {
    this.fileInputTarget.click()
  }

  fileSelected(event) {
    const file = event.target.files[0]
    if (file) this.upload(file)
    event.target.value = ""
  }

  dragOver(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.add(...DRAG_ACTIVE_CLASSES)
  }

  dragLeave(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove(...DRAG_ACTIVE_CLASSES)
  }

  drop(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove(...DRAG_ACTIVE_CLASSES)
    const file = event.dataTransfer.files[0]
    if (file) this.upload(file)
  }

  urlChanged() {
    this.showPreview(this.resolvePreviewUrl(this.inputTarget.value.trim()))
  }

  async upload(file) {
    const problem = validateImageFile(file, this.maxSizeValue, this.acceptedTypesValue)
    if (problem) {
      this.showError(problem)
      return
    }

    this.clearError()
    this.setBusy(true)
    const result = await uploadImage(this.urlValue, file, this.contextValue)
    this.setBusy(false)

    if (result.error) {
      this.showError(result.error)
      return
    }

    this.inputTarget.value = result.url
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.showPreview(this.localPreviewUrl(file) || result.preview_url)
  }

  async chooseFromLibrary(event) {
    event?.preventDefault()
    if (this.libraryBusy || this.closeLibrary) return

    this.libraryBusy = true
    this.clearError()
    try {
      const result = await fetchLibrary(this.libraryUrlValue, this.contextValue)
      if (result.error) {
        this.showError(result.error)
        return
      }
      this.closeLibrary = openLibraryPicker(
        result,
        (asset) => this.selectFromLibrary(asset),
        () => { this.closeLibrary = null }
      )
    } finally {
      this.libraryBusy = false
    }
  }

  selectFromLibrary(asset) {
    this.inputTarget.value = asset.url
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.showPreview(asset.preview_url || this.resolvePreviewUrl(asset.url))
  }

  // Private

  localPreviewUrl(file) {
    if (typeof URL.createObjectURL !== "function") return null
    this.revokeObjectUrl()
    this.objectUrl = URL.createObjectURL(file)
    return this.objectUrl
  }

  revokeObjectUrl() {
    if (this.objectUrl && typeof URL.revokeObjectURL === "function") {
      URL.revokeObjectURL(this.objectUrl)
    }
    this.objectUrl = null
  }

  resolvePreviewUrl(value) {
    if (!value) return null
    if (value.startsWith("/") && this.previewBaseValue) {
      return this.previewBaseValue.replace(/\/$/, "") + value
    }
    return value
  }

  showPreview(url) {
    if (!this.hasPreviewTarget || !this.hasPreviewImageTarget) return
    if (url) {
      this.previewImageTarget.src = url
      this.previewTarget.classList.remove("hidden")
    } else {
      this.previewImageTarget.removeAttribute("src")
      this.previewTarget.classList.add("hidden")
    }
  }

  setBusy(busy) {
    this.dropZoneTarget.classList.toggle("pointer-events-none", busy)
    this.dropZoneTarget.classList.toggle("opacity-50", busy)
    if (this.hasStatusTarget) {
      if (busy) {
        this.statusText = this.statusTarget.innerHTML
        this.statusTarget.textContent = "Uploading..."
      } else if (this.statusText) {
        this.statusTarget.innerHTML = this.statusText
      }
    }
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }
}
